// The gashapon (B16's first reward machine): Japan's capsule machine. Its 2D cabinet (Gashapon) and
// its reveal (WI-13): the capsule springs open straight onto what the twist paid, a collectible
// charm, an act key or a real prize (3D with full motion, owner's pick P6-RN72-11; flat in
// Calm/Still). The 3D cabinet is cabinet3d.ts (stage chunk).
import { motion } from 'motion/react'
import { lazy, Suspense, useState } from 'react'
import { revealFanfare } from '../../../audio/synth'
import { springs } from '../../../motion/springs'
import { t } from '../../../strings/en'
import type { RevealLook, RevealProps, RewardMachine } from '../types'
import { Gashapon } from './Gashapon'

const PrizeCapsule3D = lazy(() => import('./PrizeCapsule3D'))

/** The 3D capsule's top: deeper hexes, as colour management is off there (pile.ts). */
const DEEP: Record<string, string> = { '#FF4F4F': '#C0101A', '#2F7BFF': '#0B4FD8', '#FFC93C': '#F0A800', '#36D399': '#1FA874', '#9B6BFF': '#6B3FD8' }

/** The flat capsule (Calm/Still, or no WebGL): two halves pop apart on the reward's picture. */
export function FlatCapsule({ look, still }: { look: RevealLook; still: boolean }) {
  return (
    <>
      <motion.svg
        className="prize-capsule"
        viewBox="-10 -30 240 280"
        overflow="visible"
        aria-hidden
        initial={still ? false : { scale: 0.3, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={springs.slap}
      >
        <motion.path d="M14,110 a96,96 0 0 1 192,0z" fill={look.colour} stroke="#1B2A3A" strokeWidth="5" initial={still ? false : { y: 0 }} animate={{ y: -18, rotate: -8 }} transition={{ delay: still ? 0 : 0.25, ...springs.pop }} />
        <motion.path d="M14,124 a96,96 0 0 0 192,0z" fill="#fff" stroke="#1B2A3A" strokeWidth="5" initial={still ? false : { y: 0 }} animate={{ y: 14, rotate: 8 }} transition={{ delay: still ? 0 : 0.25, ...springs.pop }} />
      </motion.svg>
      <motion.span className={`prize-big${look.gold ? ' gold' : ''}`} aria-hidden initial={still ? false : { scale: 0 }} animate={{ scale: 1 }} transition={{ delay: still ? 0 : 0.35, ...springs.bounce }}>
        {look.art ? <img src={look.art} alt="" /> : look.kind === 'key' ? '🔑' : look.emoji}
      </motion.span>
    </>
  )
}

/** With full motion the capsule is 3D (PrizeCapsule3D); in Calm/Still or without WebGL it's flat. */
function Reveal({ look, still, calm }: RevealProps) {
  const [flat, setFlat] = useState(calm)
  return flat ? (
    <FlatCapsule look={look} still={still} />
  ) : (
    <Suspense fallback={null}>
      <PrizeCapsule3D colour={DEEP[look.colour] ?? look.colour} look={look} still={still} onFail={() => setFlat(true)} />
    </Suspense>
  )
}

export const gashapon: RewardMachine = {
  id: 'gashapon',
  words: t.machines.gashapon,
  Flat: Gashapon,
  Reveal,
  sounds: { reveal: revealFanfare },
  haptics: { spend: 'drop', open: 'crack' },
}
